import React, { FC, useEffect, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import DropdownMenu from "../../ui/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  clearOrders,
  fetchAllOrders,
} from "../../../redux/features/orderSlice";
import { RootState } from "../../../redux/app/store";
import moment from "moment";
import UpdateDispatchStatus from "./UpdateDispatchStatus";
import { cleanObject, formatNumberWithCommas } from "../../../helpers";
import { getStatusStyles } from "./util";
import ClickableUserName from "../../../components/shared/ClickableUserName";

interface OrderTableProps {
  search: string;
  startDate: Date | null;
  endDate: Date | null;
  status: string;
  filterByDispatch: string;
}

const OrderTable: FC<OrderTableProps> = ({
  search,
  startDate,
  endDate,
  status,
  filterByDispatch,
}) => {
  const [openUpdateDispatchModal, setOpenUpdateDispatchModal] = useState(false);
  const { orders, loading, error } = useAppSelector(
    (state: RootState) => state.orders,
  );
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const { user } = useAppSelector((state) => state.auth);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const columns = [
    { label: "Contract", field: "contract_id" },
    { label: "Name of Vendor", field: "vendor_name" },
    { label: "Name of Buyer", field: "buyer_name" },
    { label: "Order Date", field: "order_date" },
    { label: "Dispatch Status", field: "dispatch_status" },
    { label: "Order Status", field: "status" },
    { label: "Order Weight(Kg)", field: "order_weight" },
    { label: "Pickup Location", field: "pickup_location" },
    { label: "Contract Code", field: "contract_code" },
    { label: "Order Amount", field: "order_amount" },
    { label: "Actual Courier Fee", field: "actual_fee" },
    { label: "Estimated Courier Fee", field: "estimated_fee" },
    // { label: "Courier Balance Status", field: "courier_status" },
    { label: "Action", field: "action" },
  ];

  const dropdownOptions = [
    {
      label: "View order details",
      onClick: (id: string) => navigate(`/order-details/${id}`),
      returnBy: "id",
    },
    // {
    //   label: "Confirm balance paid",
    //   onClick: (item: any) => {
    //     setOpenEditModal(true);
    //     setOrderToEdit(item);
    //   },
    //   returnAll: true,
    // },
    {
      label: "Update order location",
      onClick: (item: any) => {
        setOpenUpdateDispatchModal(true);
        setOrderToEdit(item);
      },
      returnAll: true,
    },
  ];

  const orderData = orders?.data?.data?.map((order: any, index: number) => {
    const isPaid = order?.status === "PAID" || order?.status === "COMPLETED";
    const payBalance = order?.courierFeePaymentStatus === "PENDING";

    // Filter dropdown options for COURIER_AGENT role
    const filteredDropdownOptions = dropdownOptions.filter((option) => {
      if (option.label === "Update order location") {
        return (
          user?.user?.roles === "COURIER_AGENT" &&
          isPaid &&
          order?.dispatchStatus !== "Arrived"
        );
      } else if (option.label === "Confirm balance paid") {
        return user?.user?.roles === "COURIER_AGENT" && payBalance;
      }
      return true;
    });

    return {
      _orderId: order?.id,
      id: index + 1,
      contract_id: order?.contract?.title,
      order_weight: order?.contract?.actualWeight ?? "N/A",
      vendor_name: <ClickableUserName user={order?.vendor} userType="VENDOR" />,
      buyer_name: <ClickableUserName user={order?.buyer} userType="BUYER" />,
      pickup_location: order?.pickupLocation?.address,
      contract_code: order?.contract?.contractCode,
      order_date: moment(order?.createdAt).format("Do MMM YYYY"),
      order_amount: `₦${formatNumberWithCommas(order?.orderAmount)}`,
      dispute_reason: "Poor product quality",
      actual_fee: "₦" + formatNumberWithCommas(order?.actualCourierFee),
      estimated_fee: "₦" + formatNumberWithCommas(order?.estimatedCourierFee),
      courier_status: (
        <span
          className={`text-xs px-3 py-1 rounded-md font-semibold ${
            order?.courierFeePaymentStatus === "PAID" &&
            "bg-green-50 text-green-500 border border-green-500"
          }
               ${
                 order?.courierFeePaymentStatus === "PENDING" &&
                 "bg-yellow-50 text-yellow-500 border border-yellow-500"
               }`}
        >
          {order?.courierFeePaymentStatus}
        </span>
      ),
      status: (
        <span
          className={`text-xs px-3 py-1 rounded-md font-semibold ${
            isPaid
              ? "bg-green-50 text-green-500 border border-green-500"
              : order?.status === "PENDING"
                ? "bg-yellow-50 text-yellow-500 border border-yellow-500"
                : "bg-red-50 text-red-500 border border-red-500"
          }`}
        >
          {order?.status}
        </span>
      ),

      dispatch_status: (
        <span
          className={`text-xs px-3 py-1 rounded-md font-semibold ${getStatusStyles(
            order?.dispatchStatus,
          )}`}
        >
          {order?.dispatchStatus}
        </span>
      ),
      action: (
        <DropdownMenu
          options={filteredDropdownOptions}
          icon={<MoreVertical style={{ cursor: "pointer" }} className="z-0" />}
          item={order}
        />
      ),
    };
  });

  useEffect(() => {
    const params = cleanObject({
      limit: pageLimit,
      page: page + 1,
      filter: search,
      startDate,
      endDate,
      status,
      dispatchStatus: filterByDispatch,
    });
    if (user?.user?.roles === "COURIER_AGENT" && search) {
      dispatch(fetchAllOrders(params));
    }
    if (
      user?.user?.roles === "COURIER_ADMIN" ||
      user?.user?.roles === "SUPER_ADMIN"
    ) {
      dispatch(fetchAllOrders(params));
    }
  }, [
    dispatch,
    page,
    pageLimit,
    status,
    search,
    filterByDispatch,
    user,
    startDate,
    endDate,
  ]);

  const handleRetry = () => {
    const params = cleanObject({
      limit: pageLimit,
      page: page + 1,
      filter: search,
      startDate,
      endDate,
      status,
      dispatchStatus: filterByDispatch,
    });
    dispatch(fetchAllOrders(params));
  };

  return (
    <>
      {error && !loading && !orders?.data?.data?.length && (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
          <p className="text-gray-500 text-sm mb-3">
            Failed to load orders. Please check your connection and try again.
          </p>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:opacity-90 transition-opacity"
          >
            Retry
          </button>
        </div>
      )}
      {error && !loading && (orders?.data?.data?.length ?? 0) > 0 && (
        <div className="flex items-center justify-between mx-6 mt-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-700 text-xs">
            Failed to refresh orders. Showing previously loaded data.
          </p>
          <button
            onClick={handleRetry}
            className="ml-3 px-3 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded-md transition-colors"
          >
            Retry
          </button>
        </div>
      )}
      <CustomTable
        data={{
          columns,
          rows: orderData,
        }}
        onRowClick={(row) => navigate(`/order-details/${row._orderId}`)}
        noRecordText="No Order Available"
        loading={loading}
        showFooter={true}
        totalPages={orders?.data?.totalPages}
        itemsPerPage={orders?.data?.itemsPerPage}
        currentPage={page}
        onPageChange={(value: number) => {
          setPage(value);
        }}
        onLimitChange={(value: number) => {
          setPageLimit(value);
          setPage(0);
        }}
      />
      <UpdateDispatchStatus
        isOpen={openUpdateDispatchModal}
        onClose={() => setOpenUpdateDispatchModal(false)}
        order={orderToEdit}
        onSave={() => {
          dispatch(clearOrders());
          if (user?.user?.roles === "COURIER_AGENT" && search) {
            dispatch(fetchAllOrders({ filter: search }));
          } else {
            dispatch(
              fetchAllOrders({ limit: pageLimit, page: page + 1, status }),
            );
          }
          setOrderToEdit(null);
        }}
      />
    </>
  );
};

export default OrderTable;
